import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from 'react';

import { cn } from '../lib/cn';

/*
 * Radix handles the parts that are easy to get subtly wrong: focus trap,
 * Escape, scroll lock, `aria-modal`, and restoring focus to the trigger on
 * close. A hand-rolled dialog usually gets four of those five.
 */

export const Dialog = RadixDialog.Root;
export const DialogTrigger = RadixDialog.Trigger;
export const DialogClose = RadixDialog.Close;
export const DialogTitle = RadixDialog.Title;
export const DialogDescription = RadixDialog.Description;

export const DialogContent = forwardRef<
  ElementRef<typeof RadixDialog.Content>,
  ComponentPropsWithoutRef<typeof RadixDialog.Content> & { closeLabel: string }
>(function DialogContent({ className, children, closeLabel, ...props }, ref) {
  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay
        className={cn(
          'fixed inset-0 z-50 bg-overlay',
          'data-[state=open]:animate-in data-[state=open]:fade-in',
        )}
      />
      <RadixDialog.Content
        ref={ref}
        className={cn(
          'fixed start-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-lg',
          '-translate-x-1/2 -translate-y-1/2 rtl:translate-x-1/2',
          'rounded-lg border border-border bg-surface p-6 shadow-lg',
          'max-h-[calc(100dvh-4rem)] overflow-y-auto',
          className,
        )}
        {...props}
      >
        {children}
        <RadixDialog.Close
          className={cn(
            'absolute end-4 top-4 rounded-sm p-1 text-text-muted',
            'transition-colors hover:bg-surface-hover hover:text-text',
          )}
        >
          <X className="size-4" aria-hidden="true" />
          <span className="hs-sr-only">{closeLabel}</span>
        </RadixDialog.Close>
      </RadixDialog.Content>
    </RadixDialog.Portal>
  );
});

export function DialogHeader({ children }: { children: ReactNode }) {
  return <div className="mb-4 flex flex-col gap-1.5 pe-8">{children}</div>;
}

export function DialogFooter({ children }: { children: ReactNode }) {
  return <div className="mt-6 flex flex-wrap justify-end gap-3">{children}</div>;
}
